import { useEffect, useState } from "react";
import { CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { WelcomePacketView } from "@/components/welcome-packet/WelcomePacketView";
import { ReferralCodeCard } from "@/components/ReferralCodeCard";

type SuccessKind = "class" | "popup_vendor" | "popup_early_bird";

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sessionKind, setSessionKind] = useState<SuccessKind>("class");
  const [loading, setLoading] = useState(true);
  const [classSlug, setClassSlug] = useState<string | null>(null);

  const sessionId = searchParams.get("session_id");
  const type = searchParams.get("type");
  const kind = searchParams.get("kind");

  useEffect(() => {
    // Always confirm the session with the server. Besides telling us which kind
    // of confirmation to show, this call reconciles the registration if the
    // Stripe webhook never landed — so a parent is never charged without a
    // registration record.
    const resolveSession = async () => {
      if (!sessionId) {
        setSessionKind(type === "popup" ? (kind === "early_bird" ? "popup_early_bird" : "popup_vendor") : "class");
        setLoading(false);
        return;
      }
      try {
        const { data, error } = await supabase.functions.invoke("get-stripe-session", {
          body: { sessionId },
        });
        if (error || !data?.session) {
          setSessionKind(type === "popup" ? (kind === "early_bird" ? "popup_early_bird" : "popup_vendor") : "class");
        } else {
          const popupKind = data.session.metadata?.popup_kind;
          if (!popupKind && data.session.metadata?.class_id) {
            setClassSlug(String(data.session.metadata.class_id));
          }
          setSessionKind(popupKind === "early_bird" ? "popup_early_bird" : popupKind ? "popup_vendor" : "class");
        }
      } catch {
        setSessionKind("class");
      } finally {
        setLoading(false);
      }
    };

    resolveSession();
  }, [sessionId, type, kind]);

  const isPopup = sessionKind === "popup_vendor" || sessionKind === "popup_early_bird";

  return (
    <div className="pt-24 pb-16 px-4 min-h-screen flex items-center justify-center">
      <div className="text-center max-w-xl mx-auto space-y-6">
        <CheckCircle size={64} className="text-green-500 mx-auto" />
        <h1 className="font-heading text-3xl font-bold text-foreground">Payment Successful!</h1>

        {loading ? (
          <p className="text-muted-foreground">Loading confirmation details…</p>
        ) : (
          <>
            {sessionKind === "popup_vendor" && (
              <>
                <p className="text-muted-foreground">
                  Thank you for registering as a vendor! Your spot is confirmed.
                </p>
                <p className="text-muted-foreground">
                  You'll receive a confirmation email with event details, setup instructions, and
                  check-in information shortly.
                </p>
              </>
            )}

            {sessionKind === "popup_early_bird" && (
              <>
                <p className="text-muted-foreground">
                  Thank you for your Early Bird ticket purchase!
                </p>
                <p className="text-muted-foreground">
                  Check your email for your ticket and make sure to check your spam inbox.
                </p>
              </>
            )}

            {sessionKind === "class" && (
              <>
                <p className="text-muted-foreground">
                  Thank you for registering! You'll receive a confirmation email with class details shortly.
                </p>
                <p className="text-muted-foreground">
                  Your teacher will reach out to you a few days before class to let you know any instructions and what to bring. Keep an eye on your inbox!
                </p>
              </>
            )}

            {sessionKind === "class" && classSlug ? (
              <WelcomePacketView classSlug={classSlug} title="What you need to know" />
            ) : null}

            {sessionKind === "class" ? <ReferralCodeCard sessionId={sessionId} /> : null}

            <Button onClick={() => navigate(isPopup ? "/pop-up" : "/classes")} className="font-body font-bold">
              {isPopup ? "Back to Pop-Up" : "Back to Classes"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
